import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { NAV_GROUPS, ADMIN_GROUP, flattenNav, groupFromPathname } from "./nav-config";
import { ChangePasswordDialog } from "../components/ChangePasswordDialog";

// ---------------------------------------------------------------------------
// Brand OS top navigation (2026-10-14)
//
// Replaces the long left sidebar. A slim bar across the top with one menu
// per workflow group. Dropdowns open on click, close on outside click or
// Escape, and work with keyboard (Tab + arrows + Enter). A "Jump to…"
// search at the top-right filters every nav item as you type. On narrow
// viewports the bar collapses behind a single menu button that opens a
// full-width panel with the same groups.
//
// This component no longer scrolls on route change (the bar is a fixed
// chromeless row), so Roger's "sidebar jumps when I click" complaint
// disappears by construction.
// ---------------------------------------------------------------------------

function useOutsideClick(ref, onOutside) {
  useEffect(() => {
    function onDown(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onOutside();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [ref, onOutside]);
}

function useEscape(onEscape) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onEscape();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onEscape]);
}

function highlightLabel(label, query) {
  if (!query) return label;
  const idx = label.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return label;
  return (
    <>
      {label.slice(0, idx)}
      <mark className="bo-topnav-mark">{label.slice(idx, idx + query.length)}</mark>
      {label.slice(idx + query.length)}
    </>
  );
}

// Dropdown panel rendered under a group button or the account button.
function Dropdown({ items, activePath, onPick, labelledBy }) {
  const listRef = useRef(null);

  // Focus first enabled item when the dropdown opens so arrow keys work.
  useEffect(() => {
    const el = listRef.current?.querySelector("[data-dd-item]:not([aria-disabled='true'])");
    if (el) el.focus();
  }, []);

  function onKeyDown(e) {
    const items = Array.from(
      listRef.current?.querySelectorAll("[data-dd-item]:not([aria-disabled='true'])") || []
    );
    if (!items.length) return;
    const idx = items.indexOf(document.activeElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(idx + 1 + items.length) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    }
  }

  return (
    <div
      className="bo-topnav-dropdown"
      role="menu"
      aria-labelledby={labelledBy}
      ref={listRef}
      onKeyDown={onKeyDown}
      data-testid="topnav-dropdown"
    >
      {items.map((it) => {
        const isActive = it.end ? activePath === it.to : activePath === it.to || activePath.startsWith(it.to + "/");
        if (it.disabled) {
          return (
            <span
              key={it.to}
              className="bo-topnav-dd-item disabled"
              aria-disabled="true"
              data-dd-item
              data-testid={it.testId}
              title="Available in a later slice."
            >
              {it.label}
            </span>
          );
        }
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={!!it.end}
            className={({ isActive: active }) =>
              "bo-topnav-dd-item" + (active || isActive ? " active" : "")
            }
            role="menuitem"
            data-dd-item
            data-testid={it.testId}
            onClick={() => onPick?.()}
          >
            {it.label}
          </NavLink>
        );
      })}
    </div>
  );
}

// A single group button on the top bar + its dropdown.
// Supports three ways to open: click (opens, never closes an already-
// open group so hover + click don't fight), hover (open after a tiny
// grace period), and keyboard (focus + Enter/Space). Hover close is
// delayed slightly so the pointer can travel from button → dropdown
// without the panel vanishing in between. Any outside click or Escape
// closes.
function GroupMenu({ group, open, onOpen, onClose, activePath }) {
  const wrapRef = useRef(null);
  const hoverTimer = useRef(null);
  useOutsideClick(wrapRef, () => open && onClose());

  const activeHere = groupFromPathname(activePath) === group.key;
  const btnId = `topnav-${group.key}-button`;

  function cancelHoverTimer() {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }
  function handleMouseEnter() {
    cancelHoverTimer();
    if (window.matchMedia && window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      onOpen(group.key);
    }
  }
  function handleMouseLeave() {
    cancelHoverTimer();
    hoverTimer.current = setTimeout(() => { onClose(); }, 200);
  }
  useEffect(() => () => cancelHoverTimer(), []);

  return (
    <div
      className="bo-topnav-group"
      ref={wrapRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        id={btnId}
        type="button"
        className={"bo-topnav-group-btn" + (activeHere ? " active" : "") + (open ? " open" : "")}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        onClick={() => onOpen(group.key)}
        onFocus={cancelHoverTimer}
        data-testid={group.testId}
      >
        {group.label}
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden="true"
          className="bo-topnav-chevron"
        >
          <path d="M1.5 3.5l3.5 3.5 3.5-3.5" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <Dropdown
          items={group.items}
          activePath={activePath}
          onPick={onClose}
          labelledBy={btnId}
        />
      )}
    </div>
  );
}

function InboxBell() {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const r = await api.get("/inbox/summary");
        if (!cancelled) setUnread(r.data?.unread || 0);
      } catch (_) {
        /* silently ignore — user may not be admin */
      }
    };
    poll();
    timer = setInterval(poll, 30000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, []);
  return (
    <Link
      to="/brand-os/inbox"
      className="bo-topnav-bell"
      data-testid="topbar-inbox-bell"
      title={unread ? `${unread} unread` : "Founder Inbox"}
      aria-label={unread ? `Founder Inbox, ${unread} unread` : "Founder Inbox"}
    >
      <span aria-hidden style={{ fontSize: 18, lineHeight: 1 }}>&#128276;</span>
      {unread > 0 && (
        <span
          data-testid="topbar-inbox-badge"
          className="bo-topnav-bell-badge"
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </Link>
  );
}

// "Jump to…" filter box. Shows results in a dropdown and navigates on Enter.
function JumpSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const items = useMemo(() => flattenNav().filter((it) => !it.disabled), []);
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return items
      .filter((it) =>
        it.label.toLowerCase().includes(needle) ||
        it.group.toLowerCase().includes(needle)
      )
      .slice(0, 8);
  }, [q, items]);

  useOutsideClick(wrapRef, () => setOpen(false));
  useEscape(() => setOpen(false));

  // Global shortcut: "/" focuses the Jump search when nothing else is typing.
  useEffect(() => {
    function onKey(e) {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "/") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  function pick(it) {
    setQ("");
    setOpen(false);
    navigate(it.to);
  }

  function onKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      if (results[cursor]) {
        e.preventDefault();
        pick(results[cursor]);
      }
    }
  }

  return (
    <div className="bo-topnav-jump" ref={wrapRef}>
      <input
        ref={inputRef}
        type="text"
        value={q}
        placeholder="Jump to… ( / )"
        onChange={(e) => { setQ(e.target.value); setOpen(true); setCursor(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="bo-topnav-jump-input"
        aria-label="Jump to a page"
        data-testid="topnav-jump-input"
      />
      {open && results.length > 0 && (
        <div className="bo-topnav-jump-results" role="listbox" data-testid="topnav-jump-results">
          {results.map((it, i) => (
            <button
              key={it.to}
              type="button"
              role="option"
              aria-selected={i === cursor ? "true" : "false"}
              className={"bo-topnav-jump-row" + (i === cursor ? " cursor" : "")}
              onMouseEnter={() => setCursor(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(it); }}
              data-testid={`topnav-jump-row-${it.testId}`}
            >
              <span className="bo-topnav-jump-label">{highlightLabel(it.label, q)}</span>
              <span className="bo-topnav-jump-group">{it.group}</span>
            </button>
          ))}
        </div>
      )}
      {open && q.trim() && results.length === 0 && (
        <div className="bo-topnav-jump-results empty" data-testid="topnav-jump-empty">
          No matches
        </div>
      )}
    </div>
  );
}

// Right-side account dropdown. Holds Admin links + Sign out so the top bar
// stays narrow.
function AccountMenu({ onNavigate }) {
  const { user, role, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);   // Round 16
  const wrapRef = useRef(null);
  useOutsideClick(wrapRef, () => setOpen(false));
  useEscape(() => setOpen(false));

  async function handleLogout() {
    setOpen(false);
    await logout();
    navigate("/brand-os/login", { replace: true });
  }

  return (
    <div className="bo-topnav-account" ref={wrapRef}>
      <button
        type="button"
        className={"bo-topnav-account-btn" + (open ? " open" : "")}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        onClick={() => setOpen((v) => !v)}
        data-testid="topnav-account-btn"
      >
        {role && <span className="bo-role-badge" data-testid="topbar-role">{role}</span>}
        <span className="bo-topnav-account-email">{user?.email}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className="bo-topnav-chevron">
          <path d="M1.5 3.5l3.5 3.5 3.5-3.5" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="bo-topnav-dropdown bo-topnav-dropdown-right" role="menu" data-testid="topnav-account-menu">
          <div className="bo-topnav-dd-header">
            <div className="bo-topnav-dd-header-name">{user?.display_name || user?.email}</div>
            <div className="bo-topnav-dd-header-mail">{user?.email}</div>
          </div>
          <div className="bo-topnav-dd-section">Admin</div>
          {ADMIN_GROUP.items.map((it) => {
            if (it.disabled) {
              return (
                <span
                  key={it.to}
                  className="bo-topnav-dd-item disabled"
                  aria-disabled="true"
                  data-testid={it.testId}
                  title="Available in a later slice."
                >
                  {it.label}
                </span>
              );
            }
            return (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) => "bo-topnav-dd-item" + (isActive ? " active" : "")}
                data-testid={it.testId}
                onClick={() => { setOpen(false); onNavigate?.(); }}
                role="menuitem"
              >
                {it.label}
              </NavLink>
            );
          })}
          <div className="bo-topnav-dd-section">Account</div>
          <button
            type="button"
            className="bo-topnav-dd-item"
            role="menuitem"
            onClick={() => { setOpen(false); setChangePwOpen(true); }}
            data-testid="change-password-menu-item"
          >
            Change password
          </button>
          <button
            type="button"
            className="bo-topnav-dd-item signout"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            Sign out
          </button>
        </div>
      )}
      <ChangePasswordDialog open={changePwOpen} onClose={() => setChangePwOpen(false)} />
    </div>
  );
}

// Mobile sheet: full-width panel with all groups listed.
function MobileSheet({ open, onClose, activePath }) {
  if (!open) return null;
  return (
    <div className="bo-topnav-sheet" role="dialog" aria-modal="true" data-testid="topnav-mobile-sheet">
      {NAV_GROUPS.map((g) => (
        <section className="bo-topnav-sheet-group" key={g.key}>
          <h3 className="bo-topnav-sheet-group-title">{g.label}</h3>
          {g.items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={!!it.end}
              className={({ isActive }) => "bo-topnav-sheet-item" + (isActive ? " active" : "")}
              onClick={onClose}
              data-testid={`mobile-${it.testId}`}
            >
              {it.label}
            </NavLink>
          ))}
        </section>
      ))}
      <section className="bo-topnav-sheet-group">
        <h3 className="bo-topnav-sheet-group-title">{ADMIN_GROUP.label}</h3>
        {ADMIN_GROUP.items.map((it) =>
          it.disabled ? (
            <span key={it.to} className="bo-topnav-sheet-item disabled" aria-disabled="true" data-testid={`mobile-${it.testId}`}>
              {it.label}
            </span>
          ) : (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) => "bo-topnav-sheet-item" + (isActive ? " active" : "")}
              onClick={onClose}
              data-testid={`mobile-${it.testId}`}
            >
              {it.label}
            </NavLink>
          )
        )}
      </section>
    </div>
  );
}

export default function TopNav() {
  const location = useLocation();
  const activePath = location.pathname;
  const [openGroup, setOpenGroup] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close any open dropdown whenever the route changes.
  useEffect(() => {
    setOpenGroup(null);
    setMobileOpen(false);
  }, [location.pathname]);

  useEscape(() => { setOpenGroup(null); setMobileOpen(false); });

  const openGroupByKey = useCallback((key) => {
    setOpenGroup(key);
  }, []);

  return (
    <header className="bo-topnav" data-testid="brand-os-topnav">
      <div className="bo-topnav-inner">
        <Link to="/brand-os" className="bo-topnav-brand" data-testid="topnav-brand">
          <span className="bo-topnav-brand-mark">Brand OS</span>
        </Link>

        <nav className="bo-topnav-groups" aria-label="Primary">
          {NAV_GROUPS.map((g) => (
            <GroupMenu
              key={g.key}
              group={g}
              open={openGroup === g.key}
              onOpen={openGroupByKey}
              onClose={() => setOpenGroup(null)}
              activePath={activePath}
            />
          ))}
        </nav>

        <button
          type="button"
          className="bo-topnav-burger"
          aria-label="Open navigation"
          aria-expanded={mobileOpen ? "true" : "false"}
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="topnav-burger"
        >
          <span />
          <span />
          <span />
        </button>

        <div className="bo-topnav-right">
          <JumpSearch />
          <InboxBell />
          <AccountMenu onNavigate={() => setOpenGroup(null)} />
        </div>
      </div>

      <MobileSheet open={mobileOpen} onClose={() => setMobileOpen(false)} activePath={activePath} />
    </header>
  );
}
